import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { ProyectosService } from "src/app/proyectos/proyectos.service";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { SprintModel } from "src/app/shared/model/gestion-proyectos/sprint";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { ModalSprintComponent } from "../modal-sprint/modal-sprint.component";
import { MatDialog } from "@angular/material/dialog";
import { EstatusHUCUTASK } from "src/app/utilerias/enums/estatusHU-CUEnum";
import { ModalSprintBacklogComponent } from "../modal-agregar-sprintbacklog/modal-agregar-sprintbacklog.component";
import { EstatusSprint } from "src/app/utilerias/enums/estatusSprintEnum";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "app-consulta-sprint",
  templateUrl: "./consulta-sprint.component.html",
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule],
})
export class ConsultaSprintComponent {
  @Input() proyecto!: Proyecto;
  @Output() regresar = new EventEmitter<boolean>();

  listSprints: Array<SprintModel> = [];
  consultaObject = new ConsultaList<SprintModel>();
  totalRegistros = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;

  constructor(
    private service: ProyectosService,
    private msg: Msg,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.proyecto?.id) {
      return;
    }
    this.consultaPagina(1);
  }

  consultaPagina(page = 1): void {
    this.currentPage = Math.max(page, 1);
    this.loading = this.listSprints.length === 0;
    this.cdr.detectChanges();

    this.consultaObject.regInicial = (this.currentPage - 1) * this.pageSize;
    this.consultaObject.regXPagina = this.pageSize;
    this.consultaObject.bean = new SprintModel();
    this.consultaObject.sort = "id";
    this.consultaObject.param4 = Constantes.UNO;
    this.consultaObject.param7 = this.proyecto.id.toString();

    this.service
      .consultaSprintPaginacion(this.consultaObject)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.listSprints = Array.isArray(res?.list)
            ? res.list.map((sprint) => this.mapSprint(sprint))
            : [];
          this.totalRegistros = this.resolveTotalRegistros(res, this.listSprints.length);

          const maxPages = this.totalPages;
          if (maxPages > 0 && this.currentPage > maxPages) {
            this.consultaPagina(maxPages);
            return;
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.listSprints = [];
          this.totalRegistros = 0;
          this.msg.warningCall("Error al consultar datos", null, null);
        },
      });
  }

  regresarLista(): void {
    this.regresar.emit(true);
  }

  agregaModificaSprint(row: SprintModel | null): void {
    const sprintSeleccionado = row
      ? { ...row, estatus: EstatusSprint.obtenerEnum(row.estatus) }
      : null;

    const dialogRef = this.dialog.open(ModalSprintComponent, {
      autoFocus: false,
      disableClose: true,
      maxHeight: "calc(100vh - 32px)",
      maxWidth: "calc(100vw - 32px)",
      width: "760px",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      data: { sprint: sprintSeleccionado, proyecto: this.proyecto },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  agregarHUSprint(row: SprintModel): void {
    const sprintSeleccionado = { ...row, estatus: EstatusSprint.obtenerEnum(row.estatus) };
    const dialogRef = this.dialog.open(ModalSprintBacklogComponent, {
      autoFocus: false,
      disableClose: true,
      maxHeight: "calc(100vh - 32px)",
      maxWidth: "calc(100vw - 32px)",
      width: "1040px",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      data: { sprint: sprintSeleccionado, id: this.proyecto.id },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getProjectName(): string {
    return this.proyecto?.nombre || `Proyecto #${this.proyecto?.id ?? "-"}`;
  }

  getDateRangeLabel(sprint: SprintModel): string {
    const start = sprint?.fechaInicio ? new Date(sprint.fechaInicio).toLocaleDateString() : "Sin inicio";
    const end = sprint?.fechaFin ? new Date(sprint.fechaFin).toLocaleDateString() : "Sin fin";
    return `${start} / ${end}`;
  }

  getStatusLabel(sprint: SprintModel): string {
    return sprint?.estatus || "Sin estatus";
  }

  getStatusClass(sprint: SprintModel): string {
    const status = (sprint?.estatus || "").toUpperCase();

    if (status.includes("PEND")) {
      return "is-pending";
    }

    if (status.includes("FINAL")) {
      return "is-active";
    }

    if (status.includes("PROCESO") || status.includes("PLANE")) {
      return "is-pending";
    }

    if (status.includes("CANCEL") || status.includes("INACT")) {
      return "is-inactive";
    }

    return "is-active";
  }

  getEtapaLabel(sprint: SprintModel): string {
    return sprint?.etapa?.descripcion || "Sin etapa";
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  private mapSprint(sprint: SprintModel): SprintModel {
    const etapa = this.proyecto?.etapas?.find(
      (item) => String(item.id) === String(sprint.idFase),
    );

    return {
      ...sprint,
      estatus: EstatusHUCUTASK.eliminarGuion(sprint.estatus),
      etapa,
    };
  }

  private resolveTotalRegistros(response: ConsultaList<SprintModel>, currentItems: number): number {
    const responseRecord = response as ConsultaList<SprintModel> & Record<string, unknown>;
    const candidates = [
      responseRecord?.pTotalItems,
      responseRecord?.totalItems,
      responseRecord?.totalRegistros,
      responseRecord?.param4,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
